import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { IconButton, Paper, Tooltip, Typography } from "@mui/material";
import NodeCard from "./NodeCard";
import { bezierMidpoint, bezierPath, computeCanvasSize, conditionColorSuffix, getPortPoint } from "./canvasGeometry";
import type { ConditionPortMeta } from "./canvasGeometry";
import { computeRunVisualState } from "./nodeRunState";
import type { useCanvasGraph } from "./useCanvasGraph";
import { getConditionOptions } from "../workflow.types";
import type { ConditionFieldDef, NodeDef, OutputPort, WorkflowNode, WorkflowRun } from "../workflow.types";

/** Where `edge.sourcePort` sits among its source node's condition ports, for
 * geometry — undefined for a non-condition "out" port (fixed center, no
 * index needed) or a dangling edge whose source node is missing. */
function conditionPortMeta(node: WorkflowNode | undefined, portId: string): ConditionPortMeta | undefined {
  if (!node || node.type !== "condition") return undefined;
  const options = getConditionOptions(node.config);
  const index = options.findIndex((o) => o.id === portId);
  return index === -1 ? undefined : { index, count: options.length };
}

interface WorkflowCanvasProps {
  graph: ReturnType<typeof useCanvasGraph>;
  nodeDefsById: Record<string, NodeDef>;
  conditionFields: ConditionFieldDef[];
  run: WorkflowRun | null;
}

export default function WorkflowCanvas({ graph, nodeDefsById, conditionFields, run }: WorkflowCanvasProps) {
  const {
    nodes,
    edges,
    containerRef,
    registerNodeRef,
    selectedNodeId,
    openPanel,
    selectedEdgeId,
    selectNode,
    selectEdge,
    duplicateNode,
    beginNodeDrag,
    beginNodeResize,
    drawingEdge,
    hoveredTargetId,
    beginEdgeDraw,
    deleteSelected,
    nodeRect,
    canvasPoint,
    addNode,
    zoom,
    zoomIn,
    zoomOut,
    resetZoom,
  } = graph;

  // Re-measure after every DOM commit so edge paths track live drags,
  // ref attachment on mount, and any node-height changes from text content.
  const [measureTick, forceTick] = useState(0);
  useLayoutEffect(() => {
    forceTick((t) => t + 1);
  }, [nodes]);

  const canvasSize = useMemo(
    () => computeCanvasSize(nodes, containerRef.current?.clientWidth || 0, containerRef.current?.clientHeight || 0),
    // measureTick: recompute once the viewport is actually mounted/sized, and
    // whenever a resize/layout pass happens — clientWidth/Height aren't reactive
    // state on their own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodes, measureTick],
  );

  // Click-and-drag panning on empty canvas background (never on a node/port —
  // those already stopPropagation on their own mousedown, so this only ever
  // fires for a true background drag). Scroll offsets are mutated directly via
  // refs, not React state, so a pan doesn't re-render on every pixel of motion.
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef<{ startX: number; startY: number; startScrollLeft: number; startScrollTop: number } | null>(null);
  // A pan that actually moved shouldn't also fire the background "click to
  // deselect" handler on mouseup — that would deselect the current node every
  // time the user pans past it. Only a genuine no-movement click deselects.
  const suppressNextClickRef = useRef(false);

  const handleBackgroundMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest(".wf-node")) return;
    const el = containerRef.current;
    if (!el) return;
    panRef.current = { startX: e.clientX, startY: e.clientY, startScrollLeft: el.scrollLeft, startScrollTop: el.scrollTop };
    setIsPanning(true);

    const handleMove = (moveEvent: MouseEvent) => {
      const pan = panRef.current;
      const container = containerRef.current;
      if (!pan || !container) return;
      const dx = moveEvent.clientX - pan.startX;
      const dy = moveEvent.clientY - pan.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) suppressNextClickRef.current = true;
      container.scrollLeft = pan.startScrollLeft - dx;
      container.scrollTop = pan.startScrollTop - dy;
    };
    const handleUp = () => {
      panRef.current = null;
      setIsPanning(false);
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
  };

  const handleBackgroundClick = () => {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }
    selectNode(null);
    selectEdge(null);
    openPanel(null);
  };

  // Trackpad pinch-to-zoom and Ctrl/Cmd+mouse-wheel both arrive as a wheel
  // event with ctrlKey true in every major browser — that's the only signal
  // that distinguishes "zoom" from an ordinary two-finger scroll, so a plain
  // wheel/trackpad scroll keeps panning the canvas exactly as before.
  // A real (non-passive) listener is required here — React's synthetic
  // onWheel is attached passively, so e.preventDefault() inside it is
  // silently ignored and the browser would zoom the whole page instead.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      if (e.deltaY > 0) zoomOut();
      else zoomIn();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomIn, zoomOut]);

  const { nodeStates, takenEdgeIds } = useMemo(
    () => computeRunVisualState(nodes, edges, run),
    [nodes, edges, run],
  );

  const edgePaths = useMemo(() => {
    return edges
      .map((edge) => {
        const sourceRect = nodeRect(edge.source);
        const targetRect = nodeRect(edge.target);
        if (!sourceRect || !targetRect) return null;
        const sourceNode = nodes.find((n) => n.id === edge.source);
        const from = getPortPoint(sourceRect, edge.sourcePort, conditionPortMeta(sourceNode, edge.sourcePort));
        const to = getPortPoint(targetRect, "in");
        return { edge, from, to };
      })
      .filter((v): v is { edge: (typeof edges)[number]; from: { x: number; y: number }; to: { x: number; y: number } } => !!v);
    // measureTick forces a recompute once node refs are actually attached (they're
    // null on the very first render, before any NodeCard has mounted) and on every
    // subsequent layout change; nodeRect itself is a stable function, safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edges, nodes, measureTick]);

  const drawingPreview = useMemo(() => {
    if (!drawingEdge) return null;
    const sourceRect = nodeRect(drawingEdge.sourceNodeId);
    if (!sourceRect) return null;
    const sourceNode = nodes.find((n) => n.id === drawingEdge.sourceNodeId);
    const from = getPortPoint(sourceRect, drawingEdge.sourcePort, conditionPortMeta(sourceNode, drawingEdge.sourcePort));
    return { from, to: { x: drawingEdge.x, y: drawingEdge.y } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawingEdge, nodes]);

  const handlePortMouseDown = (nodeId: string, port: OutputPort, e: React.MouseEvent) => {
    e.stopPropagation();
    beginEdgeDraw(nodeId, port, e.clientX, e.clientY);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes("application/x-workflow-node")) e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    const raw = e.dataTransfer.getData("application/x-workflow-node");
    if (!raw) return;
    e.preventDefault();
    try {
      const def: NodeDef = JSON.parse(raw);
      const point = canvasPoint(e.clientX, e.clientY);
      addNode(def, point);
    } catch {
      /* malformed drag payload — ignore */
    }
  };

  return (
    <div className="wf-canvas-outer">
    <div
      className={`wf-canvas-viewport${isPanning ? " wf-canvas-viewport--panning" : ""}`}
      ref={containerRef}
      onClick={handleBackgroundClick}
      onMouseDown={handleBackgroundMouseDown}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div
        className="wf-canvas-surface"
        style={{ width: canvasSize.width * zoom, height: canvasSize.height * zoom }}
      >
      <div
        className="wf-canvas-content"
        style={{ width: canvasSize.width, height: canvasSize.height, transform: `scale(${zoom})`, transformOrigin: "top left" }}
      >
        <svg className="wf-edge-layer" width={canvasSize.width} height={canvasSize.height}>
          <defs>
            <marker id="wf-arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8 Z" className="wf-edge__arrowhead" />
            </marker>
            <marker id="wf-arrow-yes" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8 Z" className="wf-edge__arrowhead wf-edge__arrowhead--yes" />
            </marker>
            <marker id="wf-arrow-no" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8 Z" className="wf-edge__arrowhead wf-edge__arrowhead--no" />
            </marker>
            {["opt-2", "opt-3", "opt-4", "opt-5"].map((suffix) => (
              <marker key={suffix} id={`wf-arrow-${suffix}`} markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
                <path d="M0,0 L8,4 L0,8 Z" className={`wf-edge__arrowhead wf-edge__arrowhead--${suffix}`} />
              </marker>
            ))}
          </defs>

          {edgePaths.map(({ edge, from, to }) => {
            const sourceNode = nodes.find((n) => n.id === edge.source);
            const meta = conditionPortMeta(sourceNode, edge.sourcePort);
            const suffix = meta ? conditionColorSuffix(meta.index) : null;
            const optionLabel =
              meta && sourceNode ? getConditionOptions(sourceNode.config)[meta.index]?.label : null;
            const taken = takenEdgeIds.has(edge.id);
            const selected = selectedEdgeId === edge.id;
            const mid = bezierMidpoint(from, to);
            const marker = suffix ? `wf-arrow-${suffix}` : "wf-arrow";
            const pillWidth = optionLabel ? Math.min(140, Math.max(40, optionLabel.length * 6.5 + 16)) : 40;
            return (
              <g key={edge.id}>
                <path
                  d={bezierPath(from, to)}
                  className={[
                    "wf-edge__hit",
                  ].join(" ")}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectEdge(edge.id);
                  }}
                />
                <path
                  d={bezierPath(from, to)}
                  markerEnd={`url(#${marker})`}
                  className={[
                    "wf-edge",
                    suffix ? `wf-edge--${suffix}` : "",
                    taken ? "wf-edge--taken" : "",
                    selected ? "wf-edge--selected" : "",
                  ].join(" ")}
                />
                {optionLabel && (
                  <foreignObject x={mid.x - pillWidth / 2} y={mid.y - 11} width={pillWidth} height={22}>
                    <div className={`wf-edge__pill wf-edge__pill--${suffix}`}>{optionLabel}</div>
                  </foreignObject>
                )}
                {selected && (
                  <foreignObject x={mid.x - 12} y={mid.y - 12} width={24} height={24}>
                    <button
                      type="button"
                      className="wf-edge__delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSelected();
                      }}
                      aria-label="Delete connection"
                    >
                      <CloseIcon fontSize="inherit" />
                    </button>
                  </foreignObject>
                )}
              </g>
            );
          })}

          {drawingPreview && (
            <path d={bezierPath(drawingPreview.from, drawingPreview.to)} className="wf-edge wf-edge--preview" />
          )}
        </svg>

        {nodes.map((node) => {
          const def = nodeDefsById[node.subtype];
          return (
            <NodeCard
              key={node.id}
              node={node}
              label={def?.label || node.subtype}
              description={def?.description}
              argFields={def?.argFields}
              conditionFields={conditionFields}
              runState={nodeStates[node.id] || "pending"}
              selected={selectedNodeId === node.id}
              isInputHighlighted={hoveredTargetId === node.id}
              registerRef={(el) => registerNodeRef(node.id, el)}
              onHeaderMouseDown={(e) => {
                e.stopPropagation();
                beginNodeDrag(node.id, e.clientX, e.clientY);
              }}
              onSelect={(e) => {
                e.stopPropagation();
                selectNode(node.id);
              }}
              onOpenPanel={() => openPanel(node.id)}
              onPortMouseDown={(port, e) => handlePortMouseDown(node.id, port, e)}
              onRename={(nextLabel) => graph.updateNodeConfig(node.id, { label: nextLabel })}
              onDuplicate={() => duplicateNode(node.id)}
              onResizeMouseDown={(direction, e) => beginNodeResize(node.id, direction, e.clientX, e.clientY)}
            />
          );
        })}
      </div>
      </div>
    </div>

      <Paper elevation={2} className="wf-zoom-controls">
        <Tooltip title="Zoom out">
          <IconButton size="small" onClick={zoomOut}>
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset zoom">
          <Typography
            variant="caption"
            onClick={resetZoom}
            sx={{ minWidth: 38, textAlign: "center", cursor: "pointer", userSelect: "none" }}
          >
            {Math.round(zoom * 100)}%
          </Typography>
        </Tooltip>
        <Tooltip title="Zoom in">
          <IconButton size="small" onClick={zoomIn}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
    </div>
  );
}
